import React, { useState, useEffect } from 'react';
import { RaceTime, StationPause, Duration, Team } from '../types/Team';
import { RaceTimesGrid } from './RaceTimesGrid';
import { createOrUpdatePause, deletePause } from '../api/apis';
import { PauseForm } from './PauseForm';
import { Station } from '../types/Station';
import { fetchStations } from '../api/apis';

interface RaceDetailsProps {
  team: Team;
  onTeamUpdate?: () => Promise<void>;
}

interface PauseModalData {
  isOpen: boolean;
  isEditing: boolean;
  pause?: StationPause;
}

export const RaceDetails = ({ team, onTeamUpdate }: RaceDetailsProps) => {
  const [raceData, setRaceData] = useState<{
    raceTime: RaceTime;
    stationPauses: StationPause[];
    netRaceTime: Duration;
    totalPauseTime: Duration;
  } | null>(null);

  const [stations, setStations] = useState<Station[]>([]);
  const [modalData, setModalData] = useState<PauseModalData>({
    isOpen: false,
    isEditing: false,
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const loadStations = async () => {
      try {
        const stationsData = await fetchStations();
        setStations(stationsData);
      } catch (error) {
        console.error('Failed to load stations:', error);
      }
    };

    loadStations();
  }, []);

  useEffect(() => {
    setRaceData({
      raceTime: team.raceTime,
      stationPauses: team.stationPauses,
      netRaceTime: team.netRaceTime,
      totalPauseTime: team.totalPauseTime
    });
  }, [team]);

  const formatDuration = (duration: Duration) => {
    return `${duration.hours}h ${duration.minutes}m ${duration.seconds}s`;
  };

  const formatTime = (dateString: string) => {
    return new Date(dateString).toLocaleTimeString();
  };

  const handleAddPause = () => {
    setModalData({
      isOpen: true,
      isEditing: false,
    });
  };

  const handleEditPause = (pause: StationPause) => {
    setModalData({
      isOpen: true,
      isEditing: true,
      pause,
    });
  };

  const handleDeletePause = async (stationName: string, teamId: string) => {
    if (!window.confirm('Are you sure you want to delete this pause?')) {
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const station = stations.find(s => s.name === stationName);
      if (!station) {
        throw new Error('Station not found');
      }
      await deletePause(station.id, teamId);
      if (onTeamUpdate) {
        await onTeamUpdate();
      }
    } catch (err) {
      setError('Failed to delete pause');
      console.error('Error deleting pause:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSavePause = async (pauseData: {
    stationId: string;
    startEventTime: string;
    endEventTime: string;
  }) => {
    setLoading(true);
    setError(null);

    try {
      await createOrUpdatePause(
        team.id,
        pauseData.stationId,
        pauseData.startEventTime,
        pauseData.endEventTime
      );
      setModalData({ isOpen: false, isEditing: false });
      if (onTeamUpdate) {
        await onTeamUpdate();
      }
    } catch (err) {
      setError('Failed to save pause');
      console.error('Error saving pause:', err);
    } finally {
      setLoading(false);
    }
  };

  if (!raceData) {
    return <div className="bg-white rounded-lg shadow p-6">Loading race details...</div>;
  }

  return (
    <div className="bg-white rounded-xl shadow-lg p-8 border border-gray-100">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold text-gray-800">Race Information</h2>
        <button
          onClick={handleAddPause}
          className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
        >
          Add Pause
        </button>
      </div>

      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
          {error}
        </div>
      )}

      <div className="space-y-8">
        <RaceTimesGrid 
          raceTime={raceData.raceTime}
          netRaceTime={raceData.netRaceTime}
          totalPauseTime={raceData.totalPauseTime}
        />

        <div>
          <h3 className="text-xl font-semibold text-gray-800 mb-4 text-left">Pauses</h3>
          <div className="space-y-4">
            {raceData.stationPauses.map((pause, index) => (
              <div key={index} className="border border-gray-200 rounded-lg p-4 max-w-2xl">
                <div className="flex justify-between items-start">
                  <div className="text-left">
                    <div className="font-semibold text-gray-800 mb-1">{pause.stationName}</div>
                    <div className="text-sm text-gray-600">
                      Pause: {formatTime(pause.pauseTime)} - Resume: {formatTime(pause.resumeTime)}
                    </div>
                    <div className="text-sm font-medium text-blue-600 mt-1">
                      Duration: {formatDuration(pause.pauseDuration)}
                    </div>
                  </div>
                  <div className="flex space-x-2 ml-4">
                    <button
                      onClick={() => handleEditPause(pause)}
                      className="text-blue-600 hover:text-blue-800"
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => handleDeletePause(pause.stationName, team.id)}
                      className="text-red-600 hover:text-red-800"
                    >
                      Delete
                    </button>
                  </div>
                </div>
              </div>
            ))}
            {raceData.stationPauses.length === 0 && (
              <div className="text-gray-500 text-center py-4">No pauses recorded</div>
            )}
          </div>
        </div>
      </div>

      {/* Pause Modal */}
      {modalData.isOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h3 className="text-xl font-semibold mb-4">
              {modalData.isEditing ? 'Edit Pause' : 'Add New Pause'}
            </h3>
            <PauseForm
              pause={modalData.pause}
              onSubmit={handleSavePause}
              onCancel={() => setModalData({ isOpen: false, isEditing: false })}
              loading={loading}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export type { RaceDetailsProps }; 